import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { InlineFeedbackIcon } from './inline-feedback-icon';
import { InlineFeedbackLabel } from './inline-feedback-label';
import { InlineFeedbackRoot } from './inline-feedback-root';
import type { InlineFeedbackPropsInterface } from './types/inline-feedback.types';

/**
 * @since 7.17.0
 */

export const InlineFeedback = forwardRef<HTMLParagraphElement, InlineFeedbackPropsInterface>(
  (props: InlineFeedbackPropsInterface, forwardedRef) => {
    const { tone = 'info', asChild = false, children, ...rest } = props;

    const content = useDecoratedSlotContent<'p'>({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          <InlineFeedback.Icon />
          <InlineFeedback.Label>{childContent}</InlineFeedback.Label>
        </Fragment>
      ),
    });

    return (
      <InlineFeedback.Root ref={forwardedRef} asChild={asChild} tone={tone} {...rest}>
        {content}
      </InlineFeedback.Root>
    );
  },
) as ForwardRefExoticComponent<InlineFeedbackPropsInterface> & {
  Root: typeof InlineFeedbackRoot;
  Icon: typeof InlineFeedbackIcon;
  Label: typeof InlineFeedbackLabel;
};

InlineFeedback.displayName = 'InlineFeedback';

InlineFeedback.Root = InlineFeedbackRoot;
InlineFeedback.Icon = InlineFeedbackIcon;
InlineFeedback.Label = InlineFeedbackLabel;
